import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, Check, CreditCard, Shield, Loader2, ExternalLink, Gift, Clock, XCircle } from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";

const PricingPage = ({ user, logout }) => {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const isSoloPatient = user?.role === "patient" && !user?.therapist_id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const role = isSoloPatient ? "patient" : "therapist";
        const [plansRes, subRes] = await Promise.all([
          axios.get(`${API}/subscription/plans?role=${role}`),
          axios.get(`${API}/subscription/me`, { withCredentials: true }),
        ]);
        setPlans(plansRes.data.plans);
        setSubscription(subRes.data);
      } catch (error) {
        console.error("Error fetching plans:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isSoloPatient]);

  const handleCheckout = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du paiement");
      setCheckoutLoading(null);
    }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.get(`${API}/subscription/portal`, { withCredentials: true });
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
      setPortalLoading(false);
    }
  };

  const handleCancel = async () => {
    const msg = locale === "fr"
      ? "Êtes-vous sûr(e) de vouloir résilier votre abonnement ? L'accès sera maintenu jusqu'à la fin de la période en cours."
      : "Are you sure you want to cancel your subscription? Access will be maintained until the end of the current period.";
    if (!window.confirm(msg)) return;
    setCancelLoading(true);
    try {
      await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true });
      toast.success(locale === "fr" ? "Abonnement résilié. Accès maintenu jusqu'à la fin de la période." : "Subscription cancelled. Access maintained until end of period.");
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
    setCancelLoading(false);
  };

  const isActive = subscription?.status === "active" || subscription?.status === "trialing" || subscription?.status === "covered" || subscription?.status === "owner";

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="max-w-4xl mx-auto space-y-8" data-testid="pricing-page">
        <div className="flex items-center gap-4">
          <Link to="/profile">
            <Button variant="ghost" size="icon" data-testid="pricing-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">
              {isSoloPatient
                ? (locale === "fr" ? "Abonnement Patient" : "Patient Subscription")
                : (locale === "fr" ? "Abonnements" : "Subscriptions")}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isSoloPatient
                ? (locale === "fr" ? "Accédez à tous les exercices et outils thérapeutiques" : "Access all exercises and therapeutic tools")
                : t("pricing.chooseAPlan")}
            </p>
          </div>
        </div>

        {/* Current Status */}
        {isActive && (
          <Card className="border-green-300 bg-green-50 dark:bg-green-950/30" data-testid="subscription-active-card">
            <CardContent className="p-6 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Shield className="w-8 h-8 text-green-600" />
                <div>
                  <p className="font-medium text-green-800 dark:text-green-200">
                    {subscription.status === "covered"
                      ? t("pricing.coveredByTherapist")
                      : subscription.status === "owner"
                      ? (locale === "fr" ? "Compte propriétaire" : "Owner account")
                      : subscription.status === "trialing"
                      ? (locale === "fr" ? `Essai gratuit — ${subscription.days_remaining}j restants` : `Free trial — ${subscription.days_remaining} days left`)
                      : (locale === "fr" ? "Abonnement actif" : "Active subscription")}
                  </p>
                  {subscription.current_period_end && (
                    <p className="text-sm text-green-600 dark:text-green-400">
                      {locale === "fr" ? "Prochaine facturation" : "Next billing"}: {new Date(subscription.current_period_end).toLocaleDateString(locale)}
                    </p>
                  )}
                </div>
              </div>
              {subscription.stripe_subscription_id && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handlePortal} disabled={portalLoading} className="gap-2" data-testid="manage-subscription-btn">
                    {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                    {locale === "fr" ? "Gérer" : "Manage"}
                  </Button>
                  {!subscription.cancel_at_period_end && (
                    <Button variant="ghost" size="sm" onClick={handleCancel} disabled={cancelLoading} className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid="cancel-subscription-btn">
                      {cancelLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                      {locale === "fr" ? "Résilier" : "Cancel"}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Past due warning */}
        {subscription?.status === "past_due" && (
          <Card className="border-red-300 bg-red-50 dark:bg-red-950/30">
            <CardContent className="p-6 flex items-center gap-4">
              <XCircle className="w-8 h-8 text-red-600" />
              <div>
                <p className="font-medium text-red-800 dark:text-red-200">
                  {locale === "fr" ? "Paiement en retard" : "Payment overdue"}
                </p>
                <p className="text-sm text-red-600">
                  {locale === "fr"
                    ? "Mettez à jour votre moyen de paiement sous 24h pour éviter la suspension."
                    : "Update your payment method within 24h to avoid suspension."}
                </p>
              </div>
              <Button variant="destructive" size="sm" onClick={handlePortal} className="gap-2 ml-auto">
                <CreditCard className="w-4 h-4" />
                {locale === "fr" ? "Mettre à jour" : "Update"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Plans */}
        <div className={`grid gap-6 ${plans.length > 1 ? 'md:grid-cols-2' : 'max-w-md mx-auto'}`}>
          {plans.map((plan) => {
            const isAnnual = plan.id === "therapist_annual";
            const isPatientSolo = plan.id === "patient_solo";
            return (
              <Card
                key={plan.id}
                className={`relative overflow-hidden border-2 transition-shadow hover:shadow-lg ${
                  isAnnual ? "border-primary bg-primary/5 dark:bg-primary/10" 
                  : isPatientSolo ? "border-primary bg-primary/5 dark:bg-primary/10"
                  : "border-border"
                }`}
                data-testid={`plan-${plan.id}`}
              >
                {isAnnual && (
                  <div className="absolute top-4 right-4 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-full flex items-center gap-1">
                    <Gift className="w-3 h-3" />
                    {plan.savings}
                  </div>
                )}
                {isPatientSolo && (
                  <div className="absolute top-4 right-4 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-full flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    {locale === "fr" ? "Sans engagement" : "No commitment"}
                  </div>
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-xl">{plan.name}</CardTitle>
                  <CardDescription className="space-y-1">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-foreground">{plan.price.toFixed(2).replace(".", ",")}€</span>
                      <span className="text-muted-foreground">/{plan.interval}</span>
                    </div>
                    {isAnnual && plan.original_price && (
                      <p className="text-sm text-muted-foreground">
                        <span className="line-through">{plan.original_price}€/mois</span>
                        {" → "}
                        <span className="text-primary font-medium">539€/an</span>
                      </p>
                    )}
                    {!isPatientSolo && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {locale === "fr" ? `Engagement ${plan.commitment}` : `${plan.commitment} commitment`}
                      </div>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {!isActive && !isPatientSolo && (
                    <div className="text-center text-xs text-muted-foreground py-1">
                      {locale === "fr" ? "15 jours d'essai gratuit inclus" : "15-day free trial included"}
                    </div>
                  )}

                  <Button
                    className="w-full gap-2"
                    variant={isAnnual || isPatientSolo ? "default" : "outline"}
                    disabled={isActive || checkoutLoading !== null}
                    onClick={() => handleCheckout(plan.id)}
                    data-testid={`checkout-${plan.id}`}
                  >
                    {checkoutLoading === plan.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4" />
                    )}
                    {isActive
                      ? t("pricing.alreadySubscribed")
                      : isPatientSolo
                      ? (locale === "fr" ? "S'abonner — 9€/mois" : "Subscribe — €9/month")
                      : (locale === "fr" ? "Commencer l'essai gratuit" : "Start free trial")}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info cards */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-muted/50">
            <CardContent className="p-6 text-center text-sm text-muted-foreground">
              <p>{t("pricing.patientNote")}</p>
            </CardContent>
          </Card>
          <Card className="bg-muted/50">
            <CardContent className="p-6 text-center text-sm text-muted-foreground">
              <Link to="/cgv" className="text-primary underline hover:no-underline">
                {locale === "fr" ? "Conditions Générales de Vente" : "Terms of Sale"}
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PricingPage;
